import { useRouter } from "next/router";

import { buildPlpCollectionPageSchema } from "@/common/utils/json-ld.utils";
import { JsonLd } from "@/components/common/JsonLd";
import { useSearchContext } from "@/features/search/context/SearchContext";

/**
 * The landing page described to crawlers as what it is: a collection, with the products on it
 * listed out. Machine-readable counterpart to what a visitor sees in the results grid, and the
 * shape AI shopping engines read a category page in.
 *
 * A cap on the list, because the markup is a summary of the page rather than a copy of it — the
 * first screens of results say what this collection is, and `numberOfItems` carries the real
 * total regardless.
 */
const MAX_ITEMS = 24;

export const CollectionPageSchema = ({
  description,
}: {
  /** Prefer the CMS's SEO line, as the head tags do; falls back to the service's own. */
  description?: string | null;
}) => {
  const { asPath, locale, query: routerQuery } = useRouter();
  const { route, query } = useSearchContext();

  /* Query searches are noindex and unbounded — there is no collection to describe. */
  if (route.kind !== "page") {
    return null;
  }

  /* Page 1 only: pages 2+ are the same collection, and repeating it across a paginated set is
   * what gets the markup discounted. */
  if (Number(routerQuery.page ?? 1) > 1) {
    return null;
  }

  const data = query.data;
  const title = data?.meta.page?.title;

  /* The collection is named by its page. Nothing to describe before that arrives. */
  if (!title) {
    return null;
  }

  const schema = buildPlpCollectionPageSchema({
    title,
    description: description ?? data?.meta.page?.description ?? null,
    path: asPath?.split("?")[0] ?? "",
    locale,
    totalCount: data?.pageInfo.total,
    /* The builder drops anything without a title and a slug, and returns null on an empty list —
     * so a loading or empty page emits nothing rather than an empty ItemList. */
    items: (data?.data ?? []).slice(0, MAX_ITEMS),
  });

  if (!schema) {
    return null;
  }

  return <JsonLd item={schema} />;
};
